import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * Free-gift discount codes ("Buy X get Y" discounts in Shopify Admin) don't work like normal
 * codes: Shopify's Cart API never auto-adds the free item, so a code like this is reported as
 * "not applicable" unless the customer already happens to have the gift product in their cart.
 * To fix that from the customer's side, we look the entered code up here and, if it matches,
 * add the gift product to the cart ourselves right before applying the code.
 *
 * To add a new free-gift code (e.g. for a new campaign), add an entry keyed by the code in
 * ALL CAPS. `handle` is the gift product's handle. Only set `variantTitle` if that product has
 * more than one variant and you need a specific one - most of these gift products are single-
 * variant (one color/size per handle), so it's usually safe to leave it out.
 *
 * @type {Record<string, { handle: string, variantTitle?: string }>}
 */
const FREE_GIFT_CODES = {
  '6VSURTZW': { handle: 'non-woven-tote-bag-black' },
  RJCAERRU: { handle: 'sa-og-logo-socks-1-pk-black' },
  FREEBEANIE: { handle: 'sa-og-skully-creme' },
  KJ2PB0EDK8D9: { handle: 'orbit-clip-belt-khaki' },
  FREEBELT: { handle: 'orbit-clip-belt-khaki' },
};

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;
    const normalizedCode = discountCodeValue.trim().toUpperCase();

    const giftConfig = FREE_GIFT_CODES[normalizedCode];
    if (giftConfig) {
      // Best-effort: if this fails (network error, sold out, etc.) we still let the normal
      // discount-apply flow run below - worst case the customer sees the usual "not applicable"
      // error, same as before this existed.
      await this.#ensureGiftItemInCart(giftConfig);
    } else {
      // Codes managed through the Gift Codes app (gifts.streetammo.dk) aren't in the static map
      // above - look them up dynamically instead, so linking a new code there doesn't require a
      // theme code change. Same best-effort semantics as the static path.
      await this.#ensureAppLinkedGiftItemInCart(normalizedCode);
    }

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Adds a free-gift product to the cart if it isn't already there, so a "Buy X get Y" discount
   * code has something to apply to (see FREE_GIFT_CODES above for why this is needed).
   *
   * @param {{ handle: string, variantTitle?: string }} giftConfig
   */
  async #ensureGiftItemInCart({ handle, variantTitle }) {
    try {
      const productResponse = await fetch(`/products/${handle}.js`);
      if (!productResponse.ok) return;
      const product = await productResponse.json();

      const variant = variantTitle
        ? product.variants.find((/** @type {{ title: string }} */ v) => v.title === variantTitle)
        : product.variants[0];
      if (!variant) return;

      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const alreadyInCart = cart.items.some((/** @type {{ variant_id: number }} */ item) => item.variant_id === variant.id);
      if (alreadyInCart) return;

      await fetch('/cart/add.js', fetchConfig('json', { body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }) }));
    } catch (error) {
      // Swallow errors here - see the best-effort note where this is called.
    }
  }

  /**
   * Looks up a discount code against the Gift Codes app (gifts.streetammo.dk) and, if it's
   * linked to a gift, adds that gift to the cart and sets the `gift_variant_id` cart attribute -
   * the Gift Codes discount function reads that attribute to price the matching cart line at
   * 100% off. Unlike the static FREE_GIFT_CODES map above, this doesn't rely on a Buy X get Y
   * discount code - it works with any discount type (e.g. a percentage-off code), since the
   * free pricing comes from the app's own automatic discount, not from the typed code itself.
   *
   * @param {string} normalizedCode - The discount code, already trimmed and uppercased.
   */
  async #ensureAppLinkedGiftItemInCart(normalizedCode) {
    try {
      const response = await fetch(`https://gifts.streetammo.dk/api/gift-codes/${encodeURIComponent(normalizedCode)}`);
      if (!response.ok) return;
      const data = await response.json();
      if (!data || !data.linked || !data.variantNumericId) return;

      await fetch(
        '/cart/update.js',
        fetchConfig('json', { body: JSON.stringify({ attributes: { gift_variant_id: String(data.variantNumericId) } }) })
      );

      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const alreadyInCart = cart.items.some(
        (/** @type {{ variant_id: number }} */ item) => String(item.variant_id) === String(data.variantNumericId)
      );
      if (alreadyInCart) return;

      await fetch(
        '/cart/add.js',
        fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(data.variantNumericId), quantity: 1 }] }) })
      );
    } catch (error) {
      // Swallow errors here - see the best-effort note where this is called.
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
